import React from 'react';
import { X, Sparkles } from 'lucide-react';
import { ChatMessage } from './ChatMessage.tsx';
import { ChatInput } from './ChatInput.tsx';
import { AgentStatusIndicator } from './AgentStatusIndicator.tsx';
import { useChatStore } from './chatStore.ts';

interface ChatContainerProps {
  onClose?: () => void;
  variant?: 'embedded' | 'sidebar';
}

export const ChatContainer: React.FC<ChatContainerProps> = ({ 
  onClose, 
  variant = 'embedded' 
}) => {
  const { 
    messages, 
    isLoading, 
    error, 
    sendMessage, 
    clearError, 
    loadConversationHistory, 
    isHistoryLoaded, 
    isHistoryLoading 
  } = useChatStore();
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  // Load conversation history on mount (only once globally)
  React.useEffect(() => {
    // Check if we need to load history
    const shouldLoadHistory = !isHistoryLoaded && !isHistoryLoading && 
                              messages.length <= 1; // Only default welcome message
    
    if (shouldLoadHistory) {
      console.log('🚀 Loading conversation history on chat mount...');
      loadConversationHistory();
    } else {
      console.log('🚀 Chat already has history or is loading, skipping...');
    }
  }, []); // Empty dependency array to run only once

  // Auto-scroll to bottom when new messages arrive
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);



  const handleErrorDismiss = () => {
    clearError();
  };

  const containerClasses = variant === 'sidebar'
    ? "flex flex-col h-full bg-transparent"
    : "flex flex-col bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-pink-100 h-[calc(100vh-20rem)] md:h-[400px]";

  return (
    <div className={containerClasses}>
      {/* Header */}
      {variant !== 'sidebar' && (
        <div className="flex items-center justify-between px-3 py-2 border-b border-pink-100">
          <div className="flex items-center space-x-2">
            <div className="bg-pink-100 p-1.5 rounded-full">
              <Sparkles className="w-4 h-4 text-pink-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-800">Wedding Assistant</h3>
              <p className="text-xs text-gray-500">AI-powered planning help</p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gradient-to-b from-white/50 to-pink-50/30">
        {/* Agent Status Indicator */}
        <AgentStatusIndicator />
        
        {/* History Loading Indicator */}
        {isHistoryLoading && (
          <div className="flex items-center justify-center py-4">
            <div className="flex items-center space-x-2 text-gray-500">
              <div className="w-4 h-4 border-2 border-pink-300 border-t-pink-600 rounded-full animate-spin"></div>
              <span className="text-sm">Loading conversation history...</span>
            </div>
          </div>
        )}
        
        {/* Messages */}
        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}
        
        {/* Typing Indicator */}
        {isLoading && (
          <div className="flex items-start space-x-2">
            <div className="bg-pink-100 p-1.5 rounded-full flex-shrink-0">
              <Sparkles className="w-4 h-4 text-pink-600" />
            </div>
            <div className="bg-pink-50 rounded-lg p-3 max-w-[80%]">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Wedding Agent is thinking</span>
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-200 relative">
            <div className="flex items-start space-x-2">
              <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-1" />
              <div className="flex-1">
                <div className="font-medium">Connection Error</div>
                <div className="text-xs mt-1">{error}</div>
                <div className="text-xs mt-2 text-red-500">
                  Check your internet connection and try again
                </div>
              </div>
              <button
                onClick={handleErrorDismiss}
                className="text-red-400 hover:text-red-600 text-lg leading-none"
              >
                ×
              </button>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className={`${variant === 'sidebar' ? 'p-6' : 'p-4'} border-t border-gray-200 bg-white`}>
        <ChatInput onSendMessage={sendMessage} isLoading={isLoading} />

        
      </div>
    </div>
  );
};